/**
 * VERA Sidebar Component
 * Case-based navigation following RMS organizational patterns
 */
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  makeStyles,
  tokens,
  Text,
  Badge,
} from "@fluentui/react-components";
import {
  Home24Regular,
  Home24Filled,
  Folder24Regular,
  Folder24Filled,
  FolderOpen24Regular,
  FolderOpen24Filled,
  Archive24Regular,
  Archive24Filled,
  ShieldKeyhole24Regular,
  ShieldKeyhole24Filled,
  bundleIcon,
} from "@fluentui/react-icons";

import { mockCases } from "../../data/mockData";
import type { CaseStatus } from "../../types";

const HomeIcon = bundleIcon(Home24Filled, Home24Regular);
const FolderIcon = bundleIcon(Folder24Filled, Folder24Regular);
const FolderOpenIcon = bundleIcon(FolderOpen24Filled, FolderOpen24Regular);
const ArchiveIcon = bundleIcon(Archive24Filled, Archive24Regular);
const AuditIcon = bundleIcon(ShieldKeyhole24Filled, ShieldKeyhole24Regular);

const statusConfig: Record<
  CaseStatus,
  { label: string; color: "important" | "warning" | "success" | "informative" }
> = {
  active: { label: "Active", color: "important" },
  "under-review": { label: "Review", color: "warning" },
  closed: { label: "Closed", color: "success" },
  archived: { label: "Archived", color: "informative" },
};

const useStyles = makeStyles({
  sidebar: {
    width: "280px",
    height: "100%",
    backgroundColor: "#ffffff",
    borderRight: "1px solid #e2e8f0",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: "2px 0 8px rgba(0, 0, 0, 0.03)",
  },
  nav: {
    flex: 1,
    overflow: "auto",
    padding: tokens.spacingVerticalS,
  },
  section: {
    marginBottom: tokens.spacingVerticalL,
  },
  sectionTitle: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#63666A",
    textTransform: "uppercase",
    letterSpacing: "1px",
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    marginTop: tokens.spacingVerticalS,
    fontFamily: "'Poppins', sans-serif",
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `10px ${tokens.spacingHorizontalM}`,
    borderRadius: "8px",
    cursor: "pointer",
    color: "#2D2926",
    textDecoration: "none",
    transition: "all 0.15s ease",
    margin: `2px ${tokens.spacingHorizontalS}`,
    fontFamily: "'Open Sans', sans-serif",
  },
  navItemActive: {
    backgroundColor: "#004C97",
  },
  navItemText: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  caseItem: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    padding: `10px ${tokens.spacingHorizontalM}`,
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.15s ease",
    margin: `2px ${tokens.spacingHorizontalS}`,
  },
  caseItemActive: {
    backgroundColor: "#004C97",
  },
  caseHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  caseNumber: {
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: "'Poppins', sans-serif",
  },
  caseTitle: {
    fontSize: "12px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    paddingLeft: "28px",
  },
  caseInfo: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    paddingLeft: "28px",
    marginTop: "2px",
  },
  evidenceCount: {
    fontSize: "11px",
    color: "#63666A",
  },
  footerSection: {
    borderTop: "1px solid #e2e8f0",
    padding: tokens.spacingVerticalM,
  },
  statsRow: {
    display: "flex",
    justifyContent: "space-around",
    padding: `${tokens.spacingVerticalS} 0`,
  },
  stat: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  statValue: {
    fontSize: "18px",
    fontWeight: 700,
    color: "#004C97",
    fontFamily: "'Poppins', sans-serif",
  },
  statLabel: {
    fontSize: "10px",
    color: "#63666A",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
});

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, isActive, onClick }) => {
  const styles = useStyles();
  return (
    <div
      className={`${styles.navItem} ${isActive ? styles.navItemActive : ""}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      style={isActive ? { color: "#ffffff" } : undefined}
    >
      {icon}
      <Text
        className={styles.navItemText}
        style={isActive ? { color: "#ffffff" } : undefined}
      >
        {label}
      </Text>
    </div>
  );
};

export const Sidebar: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  // Group cases by status
  const activeCases = mockCases.filter(
    (c) => c.status === "active" || c.status === "under-review"
  );
  const closedCases = mockCases.filter(
    (c) => c.status === "closed" || c.status === "archived"
  );

  const totalEvidence = mockCases.reduce((sum, c) => sum + c.evidenceCount, 0);

  return (
    <aside className={styles.sidebar}>
      <nav className={styles.nav}>
        {/* Home / Dashboard */}
        <div className={styles.section}>
          <NavItem
            icon={<HomeIcon />}
            label="Dashboard"
            isActive={location.pathname === "/"}
            onClick={() => navigate("/")}
          />
          <NavItem
            icon={<AuditIcon />}
            label="Access Audit Log"
            isActive={location.pathname === "/admin/audit"}
            onClick={() => navigate("/admin/audit")}
          />
        </div>

        {/* Active Cases */}
        <div className={styles.section}>
          <Text className={styles.sectionTitle}>Active Cases</Text>
          {activeCases.map((c) => {
            const isActive = location.pathname === `/case/${c.id}`;
            const config = statusConfig[c.status];
            return (
              <div
                key={c.id}
                className={`${styles.caseItem} ${
                  isActive ? styles.caseItemActive : ""
                }`}
                onClick={() => navigate(`/case/${c.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === "Enter" && navigate(`/case/${c.id}`)
                }
                style={
                  isActive
                    ? { color: "#ffffff" }
                    : { ":hover": { backgroundColor: "#f8f9fa" } } as React.CSSProperties
                }
              >
                <div className={styles.caseHeader}>
                  <FolderOpenIcon
                    style={isActive ? { color: "#ffffff" } : { color: "#004C97" }}
                  />
                  <Text
                    className={styles.caseNumber}
                    style={isActive ? { color: "#ffffff" } : undefined}
                  >
                    {c.caseNumber}
                  </Text>
                  <Badge
                    size="small"
                    appearance="filled"
                    color={config.color}
                  >
                    {config.label}
                  </Badge>
                </div>
                <Text
                  className={styles.caseTitle}
                  style={
                    isActive
                      ? { color: "rgba(255,255,255,0.85)" }
                      : { color: "#63666A" }
                  }
                >
                  {c.title}
                </Text>
                <div className={styles.caseInfo}>
                  <Text
                    className={styles.evidenceCount}
                    style={
                      isActive
                        ? { color: "rgba(255,255,255,0.7)" }
                        : undefined
                    }
                  >
                    {c.evidenceCount} items
                  </Text>
                </div>
              </div>
            );
          })}
        </div>

        {/* Closed Cases */}
        {closedCases.length > 0 && (
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Closed Cases</Text>
            {closedCases.map((c) => {
              const isActive = location.pathname === `/case/${c.id}`;
              const config = statusConfig[c.status];
              return (
                <div
                  key={c.id}
                  className={`${styles.caseItem} ${
                    isActive ? styles.caseItemActive : ""
                  }`}
                  onClick={() => navigate(`/case/${c.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === "Enter" && navigate(`/case/${c.id}`)
                  }
                  style={isActive ? { color: "#ffffff" } : undefined}
                >
                  <div className={styles.caseHeader}>
                    <ArchiveIcon
                      style={
                        isActive ? { color: "#ffffff" } : { color: "#63666A" }
                      }
                    />
                    <Text
                      className={styles.caseNumber}
                      style={
                        isActive
                          ? { color: "#ffffff" }
                          : { color: "#63666A" }
                      }
                    >
                      {c.caseNumber}
                    </Text>
                    <Badge
                      size="small"
                      appearance="filled"
                      color={config.color}
                    >
                      {config.label}
                    </Badge>
                  </div>
                  <Text
                    className={styles.caseTitle}
                    style={
                      isActive
                        ? { color: "rgba(255,255,255,0.85)" }
                        : { color: "#8a8d91" }
                    }
                  >
                    {c.title}
                  </Text>
                </div>
              );
            })}
          </div>
        )}
      </nav>

      {/* Footer Stats */}
      <div className={styles.footerSection}>
        <div className={styles.statsRow}>
          <div className={styles.stat}>
            <Text className={styles.statValue}>{mockCases.length}</Text>
            <Text className={styles.statLabel}>Cases</Text>
          </div>
          <div className={styles.stat}>
            <Text className={styles.statValue}>{totalEvidence}</Text>
            <Text className={styles.statLabel}>Evidence</Text>
          </div>
          <div className={styles.stat}>
            <Text className={styles.statValue}>
              {activeCases.length}
            </Text>
            <Text className={styles.statLabel}>Active</Text>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
